<template>
  <div class="padding-top padding-right">
    <c-page-section
      v-if="hasAtLeastOneAllPermission() || hasAtLeastOneTeamMemberPermission() || hasAtLeastOneOwnPermission()"
      squares
      class="q-mb-lg"
    >
      <q-toolbar ref="pageToolbarRef" class="page-toolbar" :class="{ column: isMobile }">
        <div
          :class="{
            'col-xs-3 col-md-3 filter-dropdowns-wrapper': !isMobile,
            'full-width filter-dropdowns-wrapper': isMobile,
          }"
        >
          <div class="page-toolbar-dropdown">
            <user-dropdown
              v-model:selectedItemValue="filter.collaboratorUuid"
              data-testid="user-filter-dropdown"
              :disabled="!hasAtLeastOneAllPermission() && !hasAtLeastOneTeamMemberPermission()"
              :items="collaboratorsDropdown"
              :label="t('collabNotes.collaborator')"
            />
          </div>
        </div>
        <q-separator vertical />
        <q-space />
        <q-separator vertical />
        <div
          :class="{
            'col-xs-2 col-md-2 filter-dropdowns-wrapper': !isMobile,
            'full-width filter-dropdowns-wrapper': isMobile,
          }"
        >
          <div class="page-toolbar-dropdown">
            <c-date-picker
              v-model:date="filter.date"
              data-testid="start-date-input"
              :label="t('common.selectDate')"
            ></c-date-picker>
          </div>
        </div>
        <q-separator vertical />
        <div
          :class="{
            'col-xs-2 col-md-2 filter-dropdowns-wrapper': !isMobile,
            'full-width filter-dropdowns-wrapper': isMobile,
          }"
        >
          <div class="page-toolbar-dropdown">
            <c-drop-down
              v-model:selectedItemValue="filter.type"
              data-testid="filter-types"
              identifier="filter-types-items"
              :items="itemTypeDropdown"
              :clearable="false"
              :show-search="false"
            />
          </div>
        </div>
        <q-separator vertical />
        <q-btn
          flat
          stretch
          color="primary"
          icon="mdi-download"
          label="MD"
          class="export-btn"
          data-testid="export-md-btn"
          :disable="!mergedData.data || mergedData.data.length === 0"
          @click="exportToTxt"
        >
          <q-tooltip>{{ t('common.exportMarkdown') }}</q-tooltip>
        </q-btn>

        <q-separator vertical inset />

        <q-btn
          flat
          stretch
          color="primary"
          icon="mdi-download"
          label="TXT"
          class="export-btn"
          data-testid="export-txt-btn"
          :disable="!mergedData.data || mergedData.data.length === 0"
          @click="exportToPlainTxt"
        >
          <q-tooltip>{{ t('common.exportPlainText') }}</q-tooltip>
        </q-btn>
      </q-toolbar>
    </c-page-section>

    <user-vision
      v-if="filter.collaboratorUuid && !userVisionModule.loading"
      :selected-user-uuid="filter.collaboratorUuid ?? ''"
      :user-vision="userVisionModule.userVision"
      :can-edit="false"
    />

    <q-separator />

    <q-timeline>
      <q-timeline-entry v-for="item of mergedData.data" :key="item.key" :icon="item.icon" :color="getTenantColor()">
        <collaborator-note-record
          v-if="item.type == DataType.CollaboratorNote"
          :data="item.object as CollaboratorNoteDto"
          :allow-delete="false"
          :allow-edit="false"
          @click:delete="undefined"
          @click:edit="undefined"
        />
        <one-on-one-record
          v-if="item.type == DataType.User1on1"
          :data="item.object as Collaborator1on1Dto"
          :allow-delete="false"
          :allow-edit="false"
          @click:delete="null"
          @click:edit="null"
        />
        <feedback-record
          v-if="item.type == DataType.UserFeedback"
          :collaborator-name="(item.object as UserFeedback).fullName"
          :rating-date="(item.object as UserFeedback).fromDate.toString()"
          :bad="(item.object as UserFeedback).value === CollaboratorFeedbackDto.value.BAD"
          :ok="(item.object as UserFeedback).value === CollaboratorFeedbackDto.value.GOOD"
          :great="(item.object as UserFeedback).value === CollaboratorFeedbackDto.value.GREAT"
          :none="(item.object as UserFeedback).value === CollaboratorFeedbackDto.value.NONE"
          :align="'row'"
          :show-all-icons="false"
        />
        <goal-record
          v-if="item.type == DataType.UserGoal"
          :goal-index="item.index"
          :goal="item.object as CollaboratorGoalDto"
          :allow-read="true"
          :allow-read-own="hasGoalsPermissionOwn"
          :allow-edit="false"
        />
      </q-timeline-entry>
    </q-timeline>
    <c-alert
      class="q-mt-lg"
      type="info"
      translate-message="common.noTableData"
      :show="(mergedData.data?.length ?? 0) <= 0"
      hide-close-button
    />
  </div>
</template>

<script lang="ts" setup>
import { useI18n } from 'vue-i18n';
import { computed, ref, watch } from 'vue';
import userModule from '@/store/modules/UserModule';
import collabNotesModule from '@/store/modules/CollaboratorNotesModule';
import user1on1sModule from '@/store/modules/User1on1sModule';
import userGoalsModule from '@/store/modules/UserGoalsModule';
import heatmapModule, { UserFeedback } from '@/store/modules/HeatmapModule';
import oneOnOneRecord from '@/components/user1on1s/OneOnOneRecord.vue';
import CPageSection from '@/components/_common/PageSection.vue';
import CAlert from '@/components/_common/CommonAlert.vue';
import UserDropdown from '@/components/_common/dropdown/UserDropdown.vue';
import { useWindowDimensions } from '@/composables/useWindowDimensions';
import { bp720 } from '@/constants/breakpoints';
import { useAuthStore } from '@/store/auth';
import { useAuthorization } from '@/composables/auth/useAuthorization';
import { permissions } from '@/store/modules/PositionModule';
import { UserDropdownItem } from '@/components/_common/dropdown/DropdownItem';
import { hubClient } from '@tfpl/api-client';
import userVisionModule from '@/store/modules/UserVisionModule';
import UserVision from '@/components/userVision/UserVision.vue';
import GoalRecord from '@/components/userGoals/GoalRecord.vue';
import { isoUTCFormat } from '@/utils/dateFormats';
import { format } from 'date-fns';
import CDatePicker from '@/components/_common/DatePicker.vue';
import CDropDown from '@/components/_common/dropdown/CommonDropdown.vue';
import { getTenantColor } from '@/config';
import FeedbackRecord from '@/components/collaboratorHistory/FeedbackRecord.vue';
import CollaboratorNoteRecord from '@/components/collaboratorNotes/CollaboratorNoteRecord.vue';

const { t } = useI18n();
const authStore = useAuthStore();
const { hasLoggedUserPermission } = useAuthorization();

// Compatibility aliases for template type casts after migration to @tfpl/api-client.
type CollaboratorNoteDto = hubClient.CollaboratorNoteDto;
type Collaborator1on1Dto = hubClient.Collaborator1On1Dto;
type CollaboratorGoalDto = hubClient.CollaboratorGoalDto;
// Runtime constant mirroring the feedback rating values, referenced by the template.
const CollaboratorFeedbackDto = {
  value: {
    BAD: hubClient.CollaboratorFeedbackValue.BAD,
    GOOD: hubClient.CollaboratorFeedbackValue.GOOD,
    GREAT: hubClient.CollaboratorFeedbackValue.GREAT,
    NONE: hubClient.CollaboratorFeedbackValue.NONE,
  },
} as const satisfies { value: Record<string, NonNullable<hubClient.CollaboratorFeedbackDto['value']>> };

const windowDimensions = useWindowDimensions();
const isMobile = computed(() => windowDimensions.width < bp720);

// Permission flags are computed so they react to the async (re)load of the
// logged user's permissions after login/refresh. If they were captured once at
// setup, the item-type dropdown could freeze on a stale permission snapshot.
const hasCollabNotesPermissionAll = computed(
  () =>
    hasLoggedUserPermission(permissions.HUB_COLLABORATOR_NOTES.MANAGE_ALL) ||
    hasLoggedUserPermission(permissions.HUB_COLLABORATOR_NOTES.READ_ALL),
);
const hasCollabNotesPermissionTeamMember = computed(
  () =>
    hasLoggedUserPermission(permissions.HUB_COLLABORATOR_NOTES.MANAGE_TEAM_MEMBER) ||
    hasLoggedUserPermission(permissions.HUB_COLLABORATOR_NOTES.READ_TEAM_MEMBER),
);
const hasOneOnOnePermissionOwn = computed(() => hasLoggedUserPermission(permissions.HUB_USERS_1ON1.READ_OWN));
const hasOneOnOnePermissionAll = computed(
  () =>
    hasLoggedUserPermission(permissions.HUB_USERS_1ON1.MANAGE_ALL) ||
    hasLoggedUserPermission(permissions.HUB_USERS_1ON1.READ_ALL),
);
const hasOneOnOnePermissionTeamMember = computed(
  () =>
    hasLoggedUserPermission(permissions.HUB_USERS_1ON1.MANAGE_TEAM_MEMBER) ||
    hasLoggedUserPermission(permissions.HUB_USERS_1ON1.READ_TEAM_MEMBER),
);
const hasGoalsPermissionOwn = computed(() => hasLoggedUserPermission(permissions.HUB_USERS_GOALS.READ_OWN));
const hasGoalsPermissionAll = computed(
  () =>
    hasLoggedUserPermission(permissions.HUB_USERS_GOALS.MANAGE_ALL) ||
    hasLoggedUserPermission(permissions.HUB_USERS_GOALS.READ_ALL),
);
const hasGoalsPermissionTeamMember = computed(
  () =>
    hasLoggedUserPermission(permissions.HUB_USERS_GOALS.MANAGE_TEAM_MEMBER) ||
    hasLoggedUserPermission(permissions.HUB_USERS_GOALS.READ_TEAM_MEMBER),
);
const hasFeedbackPermissionOwn = computed(() => hasLoggedUserPermission(permissions.HUB_FEEDBACKS.READ_OWN));
const hasFeedbackPermissionAll = computed(() => hasLoggedUserPermission(permissions.HUB_FEEDBACKS.READ_ALL));
const hasFeedbackPermissionTeamMember = computed(() =>
  hasLoggedUserPermission(permissions.HUB_FEEDBACKS.READ_TEAM_MEMBER),
);

const currentUserUuid = authStore.currentUser ? authStore.currentUser.uuid : undefined;

enum itemTypes {
  OneOnOne,
  Feedback,
  CollabNote,
  Goals,
  All,
}

/**
 * Permissions
 */
const hasAtLeastOneAllPermission = () => {
  return (
    hasCollabNotesPermissionAll.value ||
    hasOneOnOnePermissionAll.value ||
    hasGoalsPermissionAll.value ||
    hasFeedbackPermissionAll.value
  );
};

const hasAtLeastOneTeamMemberPermission = () => {
  return (
    hasCollabNotesPermissionTeamMember.value ||
    hasOneOnOnePermissionTeamMember.value ||
    hasGoalsPermissionTeamMember.value ||
    hasFeedbackPermissionTeamMember.value
  );
};

const hasAtLeastOneOwnPermission = () => {
  return hasOneOnOnePermissionOwn.value || hasGoalsPermissionOwn.value || hasFeedbackPermissionOwn.value;
};

const hasOneOnOnePermissionAny = () => {
  return hasOneOnOnePermissionAll.value || hasOneOnOnePermissionOwn.value || hasOneOnOnePermissionTeamMember.value;
};

const hasCollabNotesPermissionAny = () => {
  return hasCollabNotesPermissionAll.value || hasCollabNotesPermissionTeamMember.value;
};

const hasFeedbackPermissionAny = () => {
  return hasFeedbackPermissionAll.value || hasFeedbackPermissionOwn.value || hasFeedbackPermissionTeamMember.value;
};

const hasGoalsPermissionAny = () => {
  return hasGoalsPermissionAll.value || hasGoalsPermissionOwn.value || hasGoalsPermissionTeamMember.value;
};

/**
 * Item types
 */

const itemTypeDropdown = computed(() => {
  const items = [
    {
      name: t('common.all'),
      value: itemTypes.All,
    },
  ];

  if (hasOneOnOnePermissionAny()) {
    items.push({
      name: t('menu.USER_1ON1S'),
      value: itemTypes.OneOnOne,
    });
  }

  if (hasFeedbackPermissionAny()) {
    items.push({
      name: t('common.feedback'),
      value: itemTypes.Feedback,
    });
  }

  if (hasCollabNotesPermissionAny()) {
    items.push({
      name: t('menu.COLLABORATOR_NOTES'),
      value: itemTypes.CollabNote,
    });
  }

  if (hasGoalsPermissionAny()) {
    items.push({
      name: t('common.goals'),
      value: itemTypes.Goals,
    });
  }

  return items;
});

/**
 * Filter
 */
const filter = ref<{
  collaboratorUuid?: string;
  date: string;
  type: itemTypes;
}>({
  collaboratorUuid: hasAtLeastOneOwnPermission() ? currentUserUuid : undefined,
  date: format(new Date(), 'yyyy-MM-dd'),
  type: itemTypes.All,
});

/**
 * Merged data
 */
enum DataType {
  CollaboratorNote,
  UserGoal,
  UserFeedback,
  User1on1,
}

const mergedData = ref<{
  data?:
    | {
        object: CollaboratorNoteDto | CollaboratorGoalDto | UserFeedback | Collaborator1on1Dto;
        date: Date;
        type: DataType;
        index: number;
        key: string;
        icon: string;
      }[]
    | null;
}>({
  data: [],
});

/**
 * Collaborators
 */
const collaboratorsDropdown = computed((): UserDropdownItem[] => {
  if (!hasAtLeastOneAllPermission() && !hasAtLeastOneTeamMemberPermission()) {
    return [];
  }

  let filteredUsers = userModule.users.filter((u) => u.isActive);

  if (!hasAtLeastOneAllPermission) {
    filteredUsers = filteredUsers.filter(
      (u) => u.responsiblePerson && u.responsiblePerson.uuid === authStore.currentUser?.uuid,
    );
  }

  return filteredUsers.map((u) => ({
    name: u.fullName,
    value: u.uuid,
    avatarUrl: u.slackAvatarUrl,
  }));
});

/**
 * Data
 */
if (hasAtLeastOneAllPermission()) {
  userModule.fetchUsers({});
} else if (hasAtLeastOneTeamMemberPermission()) {
  userModule.fetchUsers({
    collaboratorUuid: authStore.currentUser?.uuid,
    responsiblePersonUuid: authStore.currentUser?.uuid,
  });
} else {
  userModule.fetchUsers({ collaboratorUuid: authStore.currentUser?.uuid });
}

type HistoryFilter = (typeof filter)['value'];
type MergedDataEntry = NonNullable<(typeof mergedData)['value']['data']>[number];
/** The six-month window the timeline is loaded for, as ISO strings. */
type HistoryRange = { endDate: string; selectedDate: string };

/** Appends freshly fetched entries to the timeline and keeps the whole list ordered newest first. */
const appendToMergedData = (entries: MergedDataEntry[]) => {
  mergedData.value.data = mergedData.value.data?.concat(entries);
  mergedData.value.data = mergedData.value.data?.sort((itemA, itemB) => {
    return itemB.date.getTime() - itemA.date.getTime();
  });
};

/** Collab notes are written *about* somebody, so they are never shown on one's own history. */
const loadCollabNotes = (newFilter: HistoryFilter, { endDate, selectedDate }: HistoryRange) => {
  const isCollabNoteType = newFilter.type == itemTypes.CollabNote || newFilter.type == itemTypes.All;
  const isOtherCollaborator = !!newFilter.collaboratorUuid && newFilter.collaboratorUuid != currentUserUuid;
  if (!isCollabNoteType || !hasCollabNotesPermissionAny() || !isOtherCollaborator) {
    collabNotesModule.clearData();
    return;
  }

  collabNotesModule
    .fetchCollabNotes({
      collaboratorUuid: newFilter.collaboratorUuid!,
      dateFrom: endDate,
      dateTo: selectedDate,
    })
    .then(() => {
      appendToMergedData(
        collabNotesModule.collabNotes.map((cn, idx) => {
          return {
            object: cn,
            date: new Date(cn.createdAt),
            type: DataType.CollaboratorNote,
            index: idx,
            key: `cn-${cn.uuid}`,
            icon: 'mdi-text-box-edit',
          };
        }),
      );
    });
};

/** Goals come with the long-term vision, which is cleared together with them. */
const loadUserGoals = (newFilter: HistoryFilter, { endDate, selectedDate }: HistoryRange) => {
  const isGoalsType = newFilter.type == itemTypes.Goals || newFilter.type == itemTypes.All;
  const hasGoalsAccessForCollaborator =
    (newFilter.collaboratorUuid == currentUserUuid && hasGoalsPermissionOwn.value) ||
    hasGoalsPermissionAll.value ||
    hasGoalsPermissionTeamMember.value;
  if (!isGoalsType || !hasGoalsPermissionAny() || !newFilter.collaboratorUuid || !hasGoalsAccessForCollaborator) {
    userGoalsModule.clearUsers();
    userVisionModule.setUserVision(undefined);
    return;
  }

  userGoalsModule
    .fetchUserGoals({
      collaboratorUuid: newFilter.collaboratorUuid,
      dates: [endDate, selectedDate],
    })
    .then(() => {
      appendToMergedData(
        userGoalsModule.userGoals.map((ug, idx) => {
          return {
            object: ug,
            date: new Date(ug.createdAt),
            type: DataType.UserGoal,
            index: idx,
            key: `ug-${ug.id}`,
            icon: 'mdi-flag-checkered',
          };
        }),
      );
    });
  userVisionModule.fetchUserVision(newFilter.collaboratorUuid);
};

const loadUser1on1s = (newFilter: HistoryFilter, { endDate, selectedDate }: HistoryRange) => {
  const isOneOnOneType = newFilter.type == itemTypes.OneOnOne || newFilter.type == itemTypes.All;
  const hasOneOnOneAccessForCollaborator =
    (newFilter.collaboratorUuid == currentUserUuid && hasOneOnOnePermissionOwn.value) ||
    hasOneOnOnePermissionAll.value ||
    hasOneOnOnePermissionTeamMember.value;
  if (
    !isOneOnOneType ||
    !hasOneOnOnePermissionAny() ||
    !newFilter.collaboratorUuid ||
    !hasOneOnOneAccessForCollaborator
  ) {
    user1on1sModule.clearData();
    return;
  }

  user1on1sModule
    .fetchUser1on1s({
      collaboratorUuid: newFilter.collaboratorUuid,
      dateFrom: endDate,
      dateTo: selectedDate,
    })
    .then(() => {
      appendToMergedData(
        user1on1sModule.user1on1s.map((ooo, idx) => {
          return {
            object: ooo,
            date: new Date(ooo.updatedAt),
            type: DataType.User1on1,
            index: idx,
            key: `ooo-${ooo.uuid}`,
            icon: 'mdi-heart',
          };
        }),
      );
    });
};

const loadUserFeedbacks = (newFilter: HistoryFilter, { endDate, selectedDate }: HistoryRange) => {
  const isFeedbackType = newFilter.type == itemTypes.Feedback || newFilter.type == itemTypes.All;
  const hasFeedbackAccessForCollaborator =
    (newFilter.collaboratorUuid == currentUserUuid && hasFeedbackPermissionOwn.value) ||
    hasFeedbackPermissionTeamMember.value ||
    hasFeedbackPermissionAll.value;
  if (
    !isFeedbackType ||
    !hasFeedbackPermissionAny() ||
    !newFilter.collaboratorUuid ||
    !hasFeedbackAccessForCollaborator
  ) {
    heatmapModule.clearUserFeedbacks();
    return;
  }

  heatmapModule
    .fetchUserFeedbacks({
      userUuid: filter.value.collaboratorUuid,
      fromDate: endDate,
      toDate: selectedDate,
    })
    .then(() => {
      appendToMergedData(
        heatmapModule.userFeedbacks.map((fb, idx) => {
          return {
            object: fb,
            date: fb.fromDate,
            type: DataType.UserFeedback,
            index: idx,
            key: `fb-${fb.id}`,
            icon: 'mdi-emoticon-outline',
          };
        }),
      );
    });
};

/**
 * Data watch
 */
watch(
  filter,

  (newFilter) => {
    const filterDate = new Date(newFilter.date);
    const selectedDate = isoUTCFormat(filterDate);
    // we want to get data from the last 6 months
    filterDate.setMonth(filterDate.getMonth() - 6);
    const endDate = isoUTCFormat(filterDate);
    const range: HistoryRange = { endDate, selectedDate };

    mergedData.value.data = [];

    loadCollabNotes(newFilter, range);
    loadUserGoals(newFilter, range);
    loadUser1on1s(newFilter, range);
    loadUserFeedbacks(newFilter, range);
  },
  { deep: true, immediate: true },
);

const EXPORT_MAP = {
  TITLE: 'Collaborator History',
  VISION: 'Long-term vision, direction, goal',
  GOAL: 'Goal',
  GOAL_CATEGORY: 'Category',
  START_DATE: 'Start date',
  DEADLINE: 'Deadline',
  DONE: 'Done',
  ACTIVE: 'Active',
  NOTE: 'Note',
  ONE_ON_ONE: '1-on-1',
  USER_RATING: 'User Rating',
  COMPANY_RATING: 'Company Rating',
  LAST_CHANGE: 'Last change',
  UNKNOWN: 'Unknown',
  DATE: 'Date',
  FEEDBACK: 'Feedback',
  TYPE: 'Type',
  FINANCIAL: 'Financial',
} as const;

const typeLabel = (type: DataType): string => {
  switch (type) {
    case DataType.CollaboratorNote:
      return EXPORT_MAP.NOTE;
    case DataType.User1on1:
      return EXPORT_MAP.ONE_ON_ONE;
    case DataType.UserFeedback:
      return EXPORT_MAP.FEEDBACK;
    case DataType.UserGoal:
      return EXPORT_MAP.GOAL;
    default:
      return EXPORT_MAP.UNKNOWN;
  }
};

const buildVisionLines = (vision: typeof userVisionModule.userVision): string[] => {
  if (!vision) return [];
  return [
    `## ${EXPORT_MAP.VISION}`,
    `- **${EXPORT_MAP.GOAL}:** ${vision.message ?? ''}`,
    `- **${EXPORT_MAP.LAST_CHANGE}:** ${format(
      new Date(vision.updatedAt),
      'HH:mm dd. MM. yyyy',
    )} (${vision.editor?.name ?? EXPORT_MAP.UNKNOWN})`,
    '',
  ];
};

const buildCollaboratorNoteLines = (obj: CollaboratorNoteDto): string[] => [
  `- **${EXPORT_MAP.NOTE}:** ${obj.note ?? ''}`,
  `- **${EXPORT_MAP.LAST_CHANGE}:** ${format(
    new Date(obj.updatedAt),
    'HH:mm dd. MM. yyyy',
  )} (${obj.editor?.name ?? EXPORT_MAP.UNKNOWN})`,
];

const buildUser1on1Lines = (obj: Collaborator1on1Dto): string[] => [
  `- **${EXPORT_MAP.NOTE}:** ${obj.message ?? ''}`,
  `- **${EXPORT_MAP.FINANCIAL}:** ${obj.isFinancialMessage ? 'Yes' : 'No'}`,
  `- **${EXPORT_MAP.USER_RATING}:** ${obj.collaboratorRating ?? ''}`,
  `- **${EXPORT_MAP.COMPANY_RATING}:** ${obj.companyRating ?? ''}`,
  `- **${EXPORT_MAP.LAST_CHANGE}:** ${format(
    new Date(obj.updatedAt),
    'HH:mm dd. MM. yyyy',
  )} (${obj.editor?.name ?? EXPORT_MAP.UNKNOWN})`,
];

const buildUserFeedbackLines = (obj: UserFeedback): string[] => [
  `- **${EXPORT_MAP.USER_RATING}:** ${obj.value ?? ''}`,
  `- **${EXPORT_MAP.DATE}:** ${format(new Date(obj.fromDate), 'dd. MM. yyyy')}`,
];

const buildUserGoalLines = (obj: CollaboratorGoalDto): string[] => [
  `- **${EXPORT_MAP.GOAL}:** ${obj.message ?? ''}`,
  ...(obj.startDate ? [`- **${EXPORT_MAP.START_DATE}:** ${format(new Date(obj.startDate), 'dd. MM. yyyy')}`] : []),
  ...(obj.deadline ? [`- **${EXPORT_MAP.DEADLINE}:** ${format(new Date(obj.deadline), 'dd. MM. yyyy')}`] : []),
  `- **${EXPORT_MAP.DONE}:** ${obj.done ? 'Yes' : 'No'}`,
  `- **${EXPORT_MAP.ACTIVE}:** ${obj.active ? 'Yes' : 'No'}`,
  `- **${EXPORT_MAP.LAST_CHANGE}:** ${format(
    new Date(obj.createdAt),
    'HH:mm dd. MM. yyyy',
  )} (${obj.editor?.name ?? EXPORT_MAP.UNKNOWN})`,
];

const buildItemLines = (item: {
  object: CollaboratorNoteDto | CollaboratorGoalDto | UserFeedback | Collaborator1on1Dto;
  date: Date;
  type: DataType;
  index: number;
  key: string;
  icon: string;
}): string[] => {
  let details: string[] = [];
  switch (item.type) {
    case DataType.CollaboratorNote:
      details = buildCollaboratorNoteLines(item.object as CollaboratorNoteDto);
      break;
    case DataType.User1on1:
      details = buildUser1on1Lines(item.object as Collaborator1on1Dto);
      break;
    case DataType.UserFeedback:
      details = buildUserFeedbackLines(item.object as UserFeedback);
      break;
    case DataType.UserGoal:
      details = buildUserGoalLines(item.object as CollaboratorGoalDto);
      break;
  }

  const dateStr = format(item.date, 'dd. MM. yyyy');
  const headerLabel =
    item.type === DataType.UserGoal ? `${typeLabel(item.type)} #${item.index + 1}` : typeLabel(item.type);

  return [`## [${dateStr}] ${headerLabel}`, ...details, ''];
};

/**
 * Build export lines shared by both Markdown and plain-text exports
 */
const buildExportLines = (): string[] => {
  const data = mergedData.value.data;
  if (!data || data.length === 0) return [];

  const collaboratorName =
    collaboratorsDropdown.value.find((u) => u.value === filter.value.collaboratorUuid)?.name ??
    filter.value.collaboratorUuid ??
    'Unknown';
  const filterDate = filter.value.date;

  const visionLines = buildVisionLines(userVisionModule.userVision);
  const itemLines = data.flatMap((item) => buildItemLines(item));

  return [`# ${collaboratorName} – ${EXPORT_MAP.TITLE} (${filterDate})`, '', ...visionLines, ...itemLines];
};

const triggerDownload = (content: string, fileName: string) => {
  const blob = new Blob([content], { type: 'text/plain' });
  const url = globalThis.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  globalThis.URL.revokeObjectURL(url);
};

const exportToTxt = () => {
  const lines = buildExportLines();
  if (!lines.length) return;
  const collaboratorName =
    collaboratorsDropdown.value.find((u) => u.value === filter.value.collaboratorUuid)?.name ??
    filter.value.collaboratorUuid ??
    'Unknown';
  triggerDownload(lines.join('\n'), `History_${collaboratorName}_${filter.value.date}.md`);
};

const exportToPlainTxt = () => {
  const lines = buildExportLines();
  if (!lines.length) return;
  const plain = lines
    .map((line) =>
      line
        .replace(/^#{1,6}\s+/, '') // eslint-disable-line sonarjs/regular-expr -- remove # headings
        .replaceAll(/\*\*([^*]+)\*\*/g, '$1') // eslint-disable-line sonarjs/regular-expr -- remove **bold**
        .replace(/^-\s+/, '') // remove leading list dash
        .trim(),
    )
    .join('\n');
  const collaboratorName =
    collaboratorsDropdown.value.find((u) => u.value === filter.value.collaboratorUuid)?.name ??
    filter.value.collaboratorUuid ??
    'Unknown';
  triggerDownload(plain, `History_${collaboratorName}_${filter.value.date}.txt`);
};
</script>
<style lang="scss" scoped>
@import 'styles/app.variables.scss';
.page-toolbar-action {
  width: auto;
}
.page-toolbar-dropdown {
  max-width: none;
}
.rating-description {
  min-height: 0 !important;
}
:deep(.q-timeline__subtitle) {
  display: none;
}

.export-btn {
  height: 100%;
  padding: 0 16px;
}
</style>
